import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { ArrowRight, Thermometer, Zap, Shield, Award, ChevronRight, Factory, Building2, Pill, Snowflake } from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const categoryIcons = {
  'water-cooled-brine': Snowflake,
  'water-cooled-chilled-water': Thermometer,
  'ammonia': Factory,
  'ultra-low-temp': Zap,
  'custom': Shield,
};

export default function Home() {
  const [categories, setCategories] = useState([]);
  const [news, setNews] = useState([]);

  useEffect(() => {
    document.title = 'Laminate Chillers | Industrial Water Cooled Screw Chiller Manufacturer – Hyderabad';
    axios.get(`${API_URL}/api/products/categories`).then(r => setCategories(r.data)).catch(() => {});
    axios.get(`${API_URL}/api/news?limit=3`).then(r => setNews(r.data.articles || [])).catch(() => {});
  }, []);

  return (
    <div className="page-transition">
      {/* Hero */}
      <section className="hero-gradient relative overflow-hidden" style={{ background: 'linear-gradient(135deg, var(--brand-navy) 0%, #0f2847 50%, #0A1628 100%)' }}>
        <div className="absolute inset-0 opacity-10" style={{ backgroundImage: 'radial-gradient(circle at 20% 50%, var(--brand-teal), transparent 50%), radial-gradient(circle at 80% 20%, var(--brand-teal-2), transparent 40%)' }} />
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-16 sm:py-24 relative z-10">
          <div className="max-w-3xl">
            <Badge className="mb-4 text-white border-teal-500/30" style={{ background: 'rgba(14,138,138,0.2)' }}>Industrial Cooling Solutions</Badge>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight text-white mb-6" style={{ fontFamily: 'Space Grotesk' }}>
              Precision Chiller Systems<br />
              <span style={{ color: 'var(--brand-teal-2)' }}>Built to Perform</span>
            </h1>
            <p className="text-lg text-slate-300 mb-8 max-w-xl">
              Water-cooled & air-cooled screw chillers from 50 to 1,500 kW. Engineered for process cooling, pharma, data centers, and heavy industry.
            </p>
            <div className="flex flex-wrap gap-3">
              <Link to="/request-quote">
                <Button data-testid="hero-request-quote" className="text-white px-6 py-3 text-base" style={{ background: 'var(--brand-teal)' }}>
                  Request a Quote <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
              <Link to="/products">
                <Button variant="outline" className="border-slate-500 text-slate-200 hover:bg-white/10 px-6 py-3 text-base">
                  Explore Products
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Trust Strip */}
      <section className="border-b" style={{ background: 'var(--surface-1)' }}>
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
            {[
              { value: '35+', label: 'Custom Industrial Cooling Expertise' },
              { value: '500+', label: 'Successful Industrial Installations' },
              { value: '100% Tailor-Made', label: 'Engineered-to-Order Chiller Systems' },
              { value: 'Dedicated Support', label: 'Reliable Technical Assistance & Service' },
            ].map((s, i) => (
              <div key={i}>
                <div className="text-2xl font-bold" style={{ color: 'var(--brand-teal)', fontFamily: 'Space Grotesk' }}>{s.value}</div>
                <div className="text-sm text-muted-foreground">{s.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Product Categories */}
      <section className="py-14 sm:py-20" style={{ background: 'var(--surface-0)' }}>
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="mb-10">
            <h2 className="text-xl sm:text-2xl font-semibold tracking-tight" style={{ fontFamily: 'Space Grotesk', color: 'var(--brand-navy)' }}>Product Categories</h2>
            <p className="text-muted-foreground mt-1">Browse our complete range of industrial chiller systems</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {categories.map((cat) => {
              const Icon = categoryIcons[cat.slug] || Snowflake;
              return (
                <Link to={`/products/${cat.slug}`} key={cat.slug} data-testid={`category-card-${cat.slug}`}>
                  <Card className="product-card-hover border cursor-pointer h-full" style={{ background: 'var(--surface-1)' }}>
                    <CardContent className="p-6">
                      <div className="flex items-start gap-4">
                        <div className="w-10 h-10 rounded-lg flex items-center justify-center shrink-0" style={{ background: 'rgba(14,138,138,0.1)' }}>
                          <Icon className="w-5 h-5" style={{ color: 'var(--brand-teal)' }} />
                        </div>
                        <div className="flex-1">
                          <h3 className="font-semibold text-base mb-1" style={{ color: 'var(--brand-navy)', fontFamily: 'Space Grotesk' }}>{cat.name}</h3>
                          <p className="text-sm text-muted-foreground mb-2">{cat.description}</p>
                          <div className="flex items-center gap-2">
                            <Badge variant="secondary" className="text-xs">{cat.count} products</Badge>
                            <ChevronRight className="w-4 h-4 text-muted-foreground" />
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* Key Features */}
      <section className="py-14" style={{ background: 'var(--surface-2)' }}>
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <h2 className="text-xl sm:text-2xl font-semibold tracking-tight mb-8" style={{ fontFamily: 'Space Grotesk', color: 'var(--brand-navy)' }}>Why Laminate Chillers?</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              { icon: Award, title: 'Engineering Leadership', desc: 'Over 40 years of pioneering industrial cooling innovation. First to introduce Screw Compressors to the South Indian pharma market, SS304 evaporators to pharma applications, VFD-integrated chillers, and Electronic Expansion Valve (EEV) technology in India. We engineer solutions \u2014 we do not assemble products.' },
              { icon: Thermometer, title: 'Advanced Temperature Capabilities', desc: 'From precision process cooling to ultra-low temperature cascade systems reaching \u221270\u00b0C. We design multi-screw compressor chillers and custom cascade refrigeration systems tailored to demanding industrial requirements.' },
              { icon: Shield, title: 'Custom-Built, Process-Specific Solutions', desc: 'Every system is designed as per client-specific requirements \u2014 capacity, temperature, materials, compressor preference, and site conditions. Selective product portfolio. Performance-driven engineering. Long-term operational reliability.' },
            ].map((f, i) => (
              <Card key={i} style={{ background: 'var(--surface-1)' }}>
                <CardContent className="p-6">
                  <div className="w-10 h-10 rounded-lg flex items-center justify-center mb-4" style={{ background: 'rgba(14,138,138,0.1)' }}>
                    <f.icon className="w-5 h-5" style={{ color: 'var(--brand-teal)' }} />
                  </div>
                  <h3 className="font-semibold mb-2" style={{ fontFamily: 'Space Grotesk', color: 'var(--brand-navy)' }}>{f.title}</h3>
                  <p className="text-sm text-muted-foreground">{f.desc}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Latest News */}
      {news.length > 0 && (
        <section className="py-14" style={{ background: 'var(--surface-0)' }}>
          <div className="max-w-6xl mx-auto px-4 sm:px-6">
            <div className="flex items-center justify-between mb-8">
              <h2 className="text-xl sm:text-2xl font-semibold tracking-tight" style={{ fontFamily: 'Space Grotesk', color: 'var(--brand-navy)' }}>Latest News & Case Studies</h2>
              <Link to="/news" className="text-sm font-medium flex items-center gap-1" style={{ color: 'var(--brand-teal)' }}>
                View all <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {news.map((article) => (
                <Link to={`/news/${article._id}`} key={article._id}>
                  <Card className="product-card-hover h-full" style={{ background: 'var(--surface-1)' }}>
                    {article.image_url && (
                      <div className="h-40 overflow-hidden rounded-t-lg">
                        <img src={article.image_url} alt={article.title} className="w-full h-full object-cover" loading="lazy" />
                      </div>
                    )}
                    <CardContent className="p-5">
                      <Badge variant="secondary" className="text-xs mb-2">{article.category === 'case-study' ? 'Case Study' : 'News'}</Badge>
                      <h3 className="font-semibold text-sm mb-2 line-clamp-2" style={{ color: 'var(--brand-navy)', fontFamily: 'Space Grotesk' }}>{article.title}</h3>
                      <p className="text-xs text-muted-foreground line-clamp-2">{article.excerpt}</p>
                    </CardContent>
                  </Card>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* CTA */}
      <section className="py-14" style={{ background: 'var(--brand-navy)' }}>
        <div className="max-w-6xl mx-auto px-4 sm:px-6 text-center">
          <h2 className="text-2xl sm:text-3xl font-semibold tracking-tight text-white mb-4" style={{ fontFamily: 'Space Grotesk' }}>Ready to Specify Your Chiller?</h2>
          <p className="text-slate-400 mb-6 max-w-lg mx-auto">Tell us about your cooling requirements and our engineering team will provide a tailored solution within 48 hours.</p>
          <Link to="/request-quote">
            <Button className="text-white px-8 py-3 text-base" style={{ background: 'var(--brand-teal)' }}>
              Request a Quote <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
}
